import React, { useMemo, useRef, useState } from "react";
import EventNotification from "../../../interfaces/eventNotification/EventNotification";
import { getTimeAgo } from "../../../utilities/dateUtils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import { markAllNotificationsRead, markNotificationRead } from "../../../services/notificationServices/NotificationService";
import {
  renderNotificationMessage,
  renderNotificationTitle,
} from "../../../utilities/notifications/notificationUtils";
import { useNotificationState } from "../../../contexts/NotificationStateContext";
import { BellOff, Check, ChevronDown, ChevronRight } from "lucide-react";
import {
  aggregateNotifications,
  getCategoryCount,
  getNotificationCategoryLabel,
  groupNotificationsByDate,
  matchesNotificationCategory,
  NotificationCategory,
  NotificationCategoryIcon,
  NOTIFICATION_CATEGORIES,
  sortAndDedupeNotifications,
} from "../../../utilities/notifications/notificationPresentation";
import MeetingInvitationActions from "./MeetingInvitationActions";
import { signatureNotificationPath } from "../../../utilities/notifications/notificationNavigation";

interface NotificationPanelProps {
  notifications: EventNotification[];
  onClose: () => void;
  isOpen: boolean;
}

const PANEL_CATEGORIES: NotificationCategory[] = [
  "all",
  "unread",
  "tasks",
  "comments",
];

const MAX_PANEL_ITEMS = 8;

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  onClose,
  isOpen,
}) => {
  const [activeCategory, setActiveCategory] =
    useState<NotificationCategory>("all");
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const notificationListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const activeUserIdRef = useRef(currentUser?.uid);
  activeUserIdRef.current = currentUser?.uid;
  const { markAsAllReadNotification, markNotificationAsReadById } = useNotificationState();
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const sortedNotifications = useMemo(
    () => sortAndDedupeNotifications(notifications),
    [notifications],
  );

  const filteredNotifications = useMemo(
    () =>
      sortedNotifications
        .filter((notification) =>
          matchesNotificationCategory(notification, activeCategory),
        )
        .slice(0, MAX_PANEL_ITEMS),
    [sortedNotifications, activeCategory],
  );

  const groupedNotifications = useMemo(
    () =>
      groupNotificationsByDate(filteredNotifications).map((group) => ({
        ...group,
        aggregateGroups: aggregateNotifications(group.notifications, 3),
      })),
    [filteredNotifications],
  );

  const handleMarkRead = async (
    notification: EventNotification,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();

    if (notification.read || !currentUser) return;

    try {
      const authToken = await currentUser.getIdToken();
      if (currentUser.uid !== activeUserIdRef.current) return;
      markNotificationAsReadById(notification.id);
      await markNotificationRead(authToken, notification.id);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const toggleAggregateGroup = (groupId: string) => {
    setExpandedGroupIds((previous) => {
      const next = new Set(previous);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleNotificationClick = async (
    notification: EventNotification,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const link = (event.target as HTMLElement).closest("a");

    if (!notification.read && currentUser) {
      try {
        const authToken = await currentUser.getIdToken();
        if (currentUser.uid !== activeUserIdRef.current) return;
        markNotificationAsReadById(notification.id);
        await markNotificationRead(authToken, notification.id);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    if (link) {
      onClose();
      return;
    }

    navigate(signatureNotificationPath(notification.type, notification.actionUrl)
      || `/profile/notifications?message=${notification.id}`);
    onClose();
  };

  const handleViewAll = () => {
    navigate("/profile/notifications");
    onClose();
  };

  const handleMarkAllRead = async () => {
    if (!currentUser || isMarkingAllRead) return;
    const expectedUserId = currentUser.uid;
    setIsMarkingAllRead(true);
    try {
      const authToken = await currentUser.getIdToken();
      if (expectedUserId !== activeUserIdRef.current) return;
      if (await markAllNotificationsRead(authToken) &&
          expectedUserId === activeUserIdRef.current) markAsAllReadNotification();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[9998] transition-opacity duration-300 ${
          isOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
        onClick={onClose}
      />

      <div
        className={`dark-popup fixed right-4 top-[70px] z-[9999] flex max-h-[calc(100vh-100px)] w-[calc(100%-32px)] origin-top-right flex-col rounded-2xl border border-gray-200 bg-white dark:bg-slate-900 shadow-[0_16px_50px_rgba(15,23,42,0.16)] transition-all duration-300 ease-out sm:right-6 sm:w-[440px] lg:right-8 ${
          isOpen
            ? "visible translate-y-0 scale-100 opacity-100"
            : "invisible -translate-y-3 scale-95 opacity-0"
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between rounded-t-2xl border-b border-gray-100 dark:border-slate-700 px-4 py-3.5">
          <div>
            <h2 className="m-0 text-[1.05rem] font-semibold text-gray-900 dark:text-slate-100">
              Notifications
            </h2>
            <p className="m-0 mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">
              {getCategoryCount(sortedNotifications, "unread")} unread
            </p>
          </div>

          <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => void handleMarkAllRead()}
                disabled={isMarkingAllRead}
                className="rounded-lg px-2 py-1.5 text-[12px] font-semibold text-[#6D4DB3] transition-colors hover:bg-purple-50 disabled:cursor-wait disabled:opacity-50 dark:text-purple-300 dark:hover:bg-purple-400/10"
              >
                {isMarkingAllRead ? "Marking…" : "Mark all read"}
              </button>
            <button
              type="button"
              onClick={handleViewAll}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-[12px] font-semibold text-[#6D4DB3] dark:text-purple-300 transition-colors hover:bg-purple-50 dark:hover:bg-purple-400/10 hover:text-[#563A96] dark:hover:text-purple-200"
            >
              View all
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-100 dark:border-slate-700 px-3 py-2.5">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {PANEL_CATEGORIES.map((category) => {
              const definition = NOTIFICATION_CATEGORIES.find(
                (item) => item.id === category,
              );
              const count = getCategoryCount(sortedNotifications, category);
              const isActive = activeCategory === category;

              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={[
                    "shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors",
                    isActive
                      ? "dark-accent-pill bg-[#EEE9FA] dark:bg-purple-400/15 text-[#6241A5] dark:text-purple-300"
                      : "dark-interactive text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-800 dark:hover:text-zinc-100",
                  ].join(" ")}
                >
                  {definition?.shortLabel}
                  {category !== "all" && count > 0 ? ` ${count}` : ""}
                </button>
              );
            })}
          </div>
        </div>

        <div
          ref={notificationListRef}
          className="theme-scrollbar flex-1 overflow-y-auto px-3 py-3"
        >
          {groupedNotifications.length > 0 ? (
            <div className="space-y-4">
              {groupedNotifications.map((group) => (
                <section key={group.id}>
                  <div className="mb-1.5 flex items-center justify-between px-1">
                    <h3 className="m-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                      {group.label}
                    </h3>
                    <span className="text-[10px] text-gray-400">
                      {group.notifications.length}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {group.aggregateGroups.map((aggregateGroup) => {
                      const isExpanded = expandedGroupIds.has(aggregateGroup.id);

                      if (aggregateGroup.isAggregate) {
                        return (
                          <div
                            key={aggregateGroup.id}
                            className="dark-surface dark-border-subtle overflow-hidden rounded-xl border border-gray-200 bg-white dark:bg-slate-900"
                          >
                            <button
                              type="button"
                              onClick={() => toggleAggregateGroup(aggregateGroup.id)}
                              className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-slate-800"
                            >
                              <div className="dark-accent-tile flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-purple-100 bg-purple-50 dark:bg-purple-400/10 text-[#7E57C2] dark:text-purple-300">
                                <span className="text-[12px] font-semibold">
                                  {aggregateGroup.notifications.length}
                                </span>
                              </div>

                              <div className="min-w-0 flex-1">
                                <p className="m-0 text-[13px] font-semibold text-gray-900 dark:text-slate-100">
                                  {aggregateGroup.label}
                                </p>
                                <p className="m-0 mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">
                                  {aggregateGroup.unreadCount > 0
                                    ? `${aggregateGroup.unreadCount} unread`
                                    : "All read"}
                                </p>
                              </div>

                              <ChevronDown
                                size={15}
                                className={`shrink-0 text-gray-400 transition-transform ${
                                  isExpanded ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            {isExpanded && (
                              <div className="space-y-1 border-t border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60 p-2">
                                {aggregateGroup.notifications.map((notification) => (
                                  <div
                                    key={notification.id}
                                    onClick={(event) =>
                                      void handleNotificationClick(notification, event)
                                    }
                                    className={[
                                      "relative flex cursor-pointer items-start gap-3 rounded-lg border p-2.5 transition-colors hover:border-gray-300 hover:bg-white dark:hover:bg-zinc-900",
                                      notification.read
                                        ? "dark-interactive dark-border-subtle border-transparent bg-transparent"
                                        : "dark-border-brand-subtle border-purple-100 bg-[#FAF8FF] dark:bg-purple-400/10",
                                    ].join(" ")}
                                  >
                                    <NotificationCategoryIcon
                                      notification={notification}
                                      compact
                                    />

                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-start justify-between gap-2">
                                        <p className="m-0 line-clamp-1 text-[12px] font-medium text-gray-800 dark:text-slate-200">
                                          {renderNotificationTitle(notification)}
                                        </p>
                                        <span className="shrink-0 text-[9px] text-gray-400">
                                          {getTimeAgo(notification.createdAt)}
                                        </span>
                                      </div>

                                      <div className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-gray-500 dark:text-slate-400 [&_a]:font-medium [&_a]:text-[#6D4DB3] dark:[&_a]:text-purple-300 [&_a]:no-underline hover:[&_a]:underline">
                                        {renderNotificationMessage(notification)}
                                      </div>
                                      <MeetingInvitationActions
                                        notification={notification}
                                        compact
                                        onNavigate={onClose}
                                      />
                                    </div>

                                    {!notification.read && (
                                      <button
                                        type="button"
                                        onClick={(event) =>
                                          void handleMarkRead(notification, event)
                                        }
                                        className="dark-accent-tile mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-purple-100 bg-white text-[#6D4DB3] transition-colors hover:bg-purple-50"
                                        aria-label="Mark notification as read"
                                      >
                                        <Check size={13} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      const notification = aggregateGroup.notifications[0];

                      return (
                        <div
                          key={notification.id}
                          onClick={(event) =>
                            void handleNotificationClick(notification, event)
                          }
                          className={[
                            "relative flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all duration-150 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800",
                            notification.read
                              ? "dark-interactive dark-surface-muted dark-border-subtle border-gray-100 bg-white dark:bg-zinc-900"
                              : "dark-border-brand-subtle border-purple-100 bg-[#FAF8FF] dark:bg-purple-400/10",
                          ].join(" ")}
                        >
                          {!notification.read && (
                            <span className="absolute left-0 top-4 h-5 w-[3px] rounded-r-full bg-[#7E57C2]" />
                          )}

                          <NotificationCategoryIcon
                            notification={notification}
                            compact
                          />

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <p
                                className={[
                                  "m-0 line-clamp-1 text-[13px] leading-snug",
                                  notification.read
                                    ? "font-medium text-gray-700 dark:text-slate-300"
                                    : "font-semibold text-gray-900 dark:text-slate-100",
                                ].join(" ")}
                              >
                                {renderNotificationTitle(notification)}
                              </p>

                              <span className="shrink-0 pt-0.5 text-[10px] font-medium text-gray-400">
                                {getTimeAgo(notification.createdAt)}
                              </span>
                            </div>

                            <div className="mt-0.5 line-clamp-2 text-[12px] leading-relaxed text-gray-500 dark:text-slate-400 [&_a]:font-medium [&_a]:text-[#6D4DB3] dark:[&_a]:text-purple-300 [&_a]:no-underline hover:[&_a]:text-[#563A96] hover:[&_a]:underline">
                              {renderNotificationMessage(notification)}
                            </div>

                            <MeetingInvitationActions
                              notification={notification}
                              compact
                              onNavigate={onClose}
                            />

                            <span className="mt-1.5 inline-flex rounded-md bg-gray-100 dark:bg-zinc-800 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                              {getNotificationCategoryLabel(notification)}
                            </span>
                          </div>

                          {!notification.read && (
                            <button
                              type="button"
                              onClick={(event) =>
                                void handleMarkRead(notification, event)
                              }
                              className="dark-accent-tile mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-purple-100 bg-white text-[#6D4DB3] transition-colors hover:bg-purple-50"
                              aria-label="Mark notification as read"
                            >
                              <Check size={13} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="dark-border-subtle m-1 flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/60 dark:bg-slate-800/60 py-10 text-center">
              <div className="dark-surface dark-borderless mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-gray-100 bg-white dark:bg-slate-900 text-[#7E57C2] dark:text-purple-300 shadow-sm">
                <BellOff size={19} />
              </div>
              <h3 className="m-0 text-[13px] font-semibold text-gray-900 dark:text-slate-100">
                Nothing here
              </h3>
              <p className="m-0 mt-1 text-[11px] text-gray-500 dark:text-slate-400">
                No notifications match this category.
              </p>
            </div>
          )}
        </div>

        {sortedNotifications.length > MAX_PANEL_ITEMS && (
          <div className="border-t border-gray-100 dark:border-slate-700 px-4 py-2.5 text-center">
            <button
              type="button"
              onClick={handleViewAll}
              className="text-[11px] font-semibold text-[#6D4DB3] hover:text-[#563A96]"
            >
              Open notification center
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationPanel;
